import { Button } from "@/components/ui/button";
import KycForm from "@/components/kyc/kyc-form";
import KycRejected from "@/components/kyc/kyc-rejected";
import KycUnderReview from "@/components/kyc/kyc-under-review";
import { useKycLogout } from "@/components/kyc/use-kyc-logout";
import KycShell from "@/components/kyc/kyc-shell";
import Throbber from "@/components/throbber";
import { useGetUser, useGetUserKyc } from "@/hooks/use-queries";
import Modal from "@/components/modals";
import { useModalStore } from "@/zustand/modalStore";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const REQUIREMENTS = [
  {
    icon: "/images/kyc-valid-id.svg",
    title: "Valid ID document",
    description:
      "A government-issued ID such as: Passport, National ID, or Driver's License.",
  },
  {
    icon: "/images/kyc-proof-of-address.svg",
    title: "Proof of address",
    description:
      "A recent utility bill or official document showing your name and address (issued within the last 3 months).",
  },
];

const Kyc = () => {
  const [started, setStarted] = useState(false);
  const navigate = useNavigate();
  const logout = useKycLogout();
  const { openModal } = useModalStore();
  const { data: user, isLoading: isLoadingUser } = useGetUser();
  const { data: kyc, isLoading: isLoadingKyc } = useGetUserKyc();
  const isLoading = isLoadingUser || isLoadingKyc;
  // A pending submission (made now or earlier) means there's nothing left to do.
  const rejected = kyc?.status === "rejected";
  const underReview = kyc?.status === "pending";

  useEffect(() => {
    if (user?.kycExempt || kyc?.status === "approved")
      navigate("/dashboard", { replace: true });
  }, [user?.kycExempt, kyc?.status, navigate]);

  return (
    <KycShell firstName={user?.firstName}>
      {isLoading ? (
        <div className="m-auto">
          <Throbber className="text-auth-accent size-8" />
        </div>
      ) : underReview ? (
        <KycUnderReview />
      ) : started ? (
        <KycForm
          defaults={user}
          previous={rejected ? kyc : undefined}
          onBackToIntro={() => setStarted(false)}
          onSubmitted={() => setStarted(false)}
        />
      ) : rejected ? (
        <KycRejected
          reviewNote={kyc?.reviewNote}
          onResubmit={() => setStarted(true)}
        />
      ) : (
        <div className="mx-auto my-auto w-full max-w-xl py-6">
          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => openModal("referralCode")}
              className="text-auth-text mb-5 w-full cursor-pointer rounded-lg border border-dashed border-[#e2c27d] bg-[#fff7e8] px-4 py-5 text-sm font-semibold transition-colors hover:bg-[#fdf0d6]"
            >
              Use Referral Code
            </button>
            <span className="text-auth-text w-fit rounded-full bg-[#f3eee4] px-4 py-2 text-[10px] font-semibold tracking-[0.2em] uppercase">
              Welcome to Dynascent
            </span>

            <div className="mt-5 flex items-center gap-4">
              <img
                src="/images/kyc-valid-id.svg"
                alt=""
                className="size-20 shrink-0"
              />
              <div>
                <h1 className="text-2xl font-semibold">Verify your account</h1>
                <p className="mt-1 text-sm">
                  Please complete your identity verification (KYC) to unlock all
                  features and start using DigitWallet.
                </p>
              </div>
            </div>

            <h2 className="mt-6 text-lg font-semibold">
              What you&apos;ll need
            </h2>
            <p className="text-sm">
              Please have the following documents ready before you start.
            </p>

            <div className="mt-3 grid gap-3 lg:grid-cols-2 lg:gap-4">
              {REQUIREMENTS.map(({ icon, title, description }) => (
                <div
                  key={title}
                  className="border-auth-stroke flex flex-col gap-3 rounded-lg border bg-white p-5"
                >
                  <img src={icon} alt="" className="size-14" />
                  <h3 className="font-semibold">{title}</h3>
                  <p className="text-xs leading-relaxed">{description}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <Button
                className="bg-auth-action text-auth-on-action hover:bg-auth-action/90 h-11 w-full rounded"
                type="button"
                disabled={isLoading}
                onClick={() => setStarted(true)}
              >
                Start Verification
              </Button>
              <Button
                variant="outline"
                className="border-auth-action text-auth-text h-11 w-full rounded bg-transparent"
                type="button"
                onClick={logout}
              >
                I&apos;ll do this later
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* The modals are dark-themed, so scope the user theme to them. */}
      <div className="theme-user">
        <Modal />
      </div>
    </KycShell>
  );
};

export default Kyc;
